// @ts-nocheck
import type { NextPage } from "next";
import Head from "next/head";
import { useState } from "react";
import {
  getSession,
  GetSessionParams,
  useSession,
  signOut,
} from "next-auth/react";
import prisma from "@/lib/prisma";
import Link from "next/link";

import { unstable_getServerSession } from "next-auth/next";

import { authOptions } from "pages/api/auth/[...nextauth]";

import { Fragment } from "react";
import { Popover, Transition } from "@headlessui/react";
import {
  MenuAlt1Icon,
  XIcon,
  CheckIcon,
  SpeakerphoneIcon,
  LightningBoltIcon,
  ChatIcon,
  MailIcon,
  PuzzleIcon,
  UserGroupIcon,
  TemplateIcon,
  DocumentSearchIcon,
  FolderOpenIcon,
  ChevronDownIcon,
  ChartBarIcon,
  CheckCircleIcon,
  PlayIcon,
  ShieldCheckIcon,
  PhoneIcon,
  StarIcon,
  GiftIcon,
  ShoppingBagIcon,
} from "@heroicons/react/outline";

import Layout from "../components/Layout";

export async function getServerSideProps(
  context: GetSessionParams | undefined
) {
  const session = await unstable_getServerSession(
    context.req,
    context.res,
    authOptions
  );

  if (session) {
    // check if user belongs to this company
    const user = await prisma.user.findUnique({
      // @ts-ignore
      where: { email: session?.user?.email },
      include: {
        Company: true,
      },
    });

    return {
      props: {
        user: {
          id: user?.id,
          name: user?.name,
          email: user?.email,
          companyId: user?.companyId,
          role: user?.role,
        },
        company: user?.Company ? JSON.stringify(user?.Company) : {},
      },
    };
  } else {
    return {
      props: {
        user: {},
        company: {},
      },
    };
  }
}

const features = [
  "Unlimited surveys",
  "Unlimited templates",
  "Unlimited segments",
  "Custom branding",
  "Rewards and incentives",
  "Magic link authentication",
  "Third-party integrations",
  "Up to 249 recipient seats",
  "3 additional team members",
];

const transferFeatures = [
  {
    id: 1,
    name: "Interpretation to discover meaning",
    description:
      "Responses are automatically tagged by intent. Is a question being asked? Is a suggestion being made? Is an issue being raised? Or is someone or something being praised?",
    icon: DocumentSearchIcon,
  },
  {
    id: 2,
    name: "Categorization to determine priority",
    description:
      "What’s the theme of this response? Has it occurred before? Is it something urgent to address? What department or segment is this coming from?",
    icon: FolderOpenIcon,
  },
  {
    id: 3,
    name: "Tooling to take action",
    description:
      "Having insight is great, but what matters most to your employees is what you do with it. Anonymously reply to a response, assign a peer or team manager to investigate, kickoff a discussion, track and highlight trends, or publish results to showcase transparency",
    icon: LightningBoltIcon,
  },
];

const communicationFeatures = [
  {
    id: 3,
    name: "Participate and earn points",
    description:
      "Assign a point value to all surveys and anytime feedback. When a team member submits a response, they earn points.",
    icon: StarIcon,
  },
  {
    id: 1,
    name: "Cash in for rewards",
    description:
      "Create rewards like gift cards, company swag and more that employees can cash in their points on.",
    icon: GiftIcon,
  },
  {
    id: 2,
    name: "Manage redemptions & fulfillment",
    description:
      "Get alerted when a team member redeems their points, mark as fulfilled, edit reward availability and more!",
    icon: ShoppingBagIcon,
  },
];

const solutions = [
  {
    name: "Surveys",
    description: "Collect engagement, pulse, eNPS and more from your team",
    href: "#",
    icon: ChartBarIcon,
  },
  {
    name: "Insights",
    description: "Discover and unpack the deeper meaning from survey responses",
    href: "#",
    icon: ChartBarIcon,
  },
  {
    name: "Trends and Focus Areas",
    description:
      "Monitor trends over time, and zero in on the most impactful action items for your team",
    href: "#",
    icon: LightningBoltIcon,
  },
  {
    name: "1-on-1's",
    description:
      "Use your super-charged insight to drive more meaningful conversations with your team members.",
    href: "#",
    icon: LightningBoltIcon,
  },
];
const callsToAction = [
  { name: "Watch Demo", href: "#", icon: PlayIcon },
  {
    name: "Get in touch",
    href: "mailto:christian@happyteam.ai",
    icon: MailIcon,
  },
];

const footerNav = {
  solutions: [
    { name: "For your employees", href: "#" },
    { name: "For your clients", href: "#" },
    { name: "For your customers", href: "#" },
  ],
  support: [
    { name: "Pricing", href: "#" },
    { name: "Knowledgebase", href: "#" },
    { name: "Support Requests", href: "#" },
    { name: "Status Page", href: "#" },
  ],
  company: [
    { name: "About", href: "#" },
    { name: "Blog", href: "#" },
  ],
  legal: [
    { name: "Privacy", href: "#" },
    { name: "Terms", href: "#" },
  ],
  social: [
    {
      name: "Twitter",
      href: "https://twitter.com/HappyTeamAI",
      icon: (props) => (
        <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
          <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
        </svg>
      ),
    },
  ],
};

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const Home: NextPage = ({ user, company }: any) => {
  const { data: session, status } = useSession();
  const [email, setEmail] = useState("");

  const companyDetails =
    (Object.keys(company).length > 0 && JSON.parse(company)) || {};

  return (
    <div>
      <Head>
        <title>Create Next App</title>
        <meta name="description" content="Generated by create next app" />
        <link rel="icon" href="/favicon.ico" />
      </Head>

      {/* HERO */}
      <div className="relative overflow-hidden bg-white">
        <div className="mx-auto max-w-7xl">
          <div className="relative z-10 bg-white pb-8 sm:pb-16 md:pb-20 lg:w-full lg:max-w-2xl lg:pb-28 xl:pb-32">
            <svg
              className="absolute inset-y-0 right-0 hidden h-full w-48 translate-x-1/2 transform text-white lg:block"
              fill="currentColor"
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              aria-hidden="true"
            >
              <polygon points="50,0 100,0 50,100 0,100" />
            </svg>

            <Popover>
              <div className="relative pt-6">
                <nav
                  className="relative flex items-center justify-between sm:h-10 lg:justify-start"
                  aria-label="Global"
                >
                  <div className="flex flex-shrink-0 flex-grow items-center lg:flex-grow-0">
                    <div className="flex w-full items-center justify-between md:w-auto pl-3 pr-3">
                      <a href="#">
                        <svg
                          style={{ height: "40px" }}
                          viewBox="0 0 1107 284"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M429 256C395.732 279.491 357.761 277.827 323 256"
                            stroke="#5336FF"
                            strokeWidth="21"
                            strokeLinecap="round"
                          />
                          <path
                            d="M14 197V57H39.2V110H42.8C43.8667 107.867 45.5333 105.733 47.8 103.6C50.0667 101.467 53.0667 99.7333 56.8 98.4C60.6667 96.9333 65.5333 96.2 71.4 96.2C79.1333 96.2 85.8667 98 91.6 101.6C97.4667 105.067 102 109.933 105.2 116.2C108.4 122.333 110 129.533 110 137.8V197H84.8V139.8C84.8 132.333 82.9333 126.733 79.2 123C75.6 119.267 70.4 117.4 63.6 117.4C55.8667 117.4 49.8667 120 45.6 125.2C41.3333 130.267 39.2 137.4 39.2 146.6V197H14ZM168.042 199.8C160.976 199.8 154.642 198.6 149.042 196.2C143.442 193.667 138.976 190.067 135.642 185.4C132.442 180.6 130.842 174.8 130.842 168C130.842 161.2 132.442 155.533 135.642 151C138.976 146.333 143.509 142.867 149.242 140.6C155.109 138.2 161.776 137 169.242 137H196.442V131.4C196.442 126.733 194.976 122.933 192.042 120C189.109 116.933 184.442 115.4 178.042 115.4C171.776 115.4 167.109 116.867 164.042 119.8C160.976 122.6 158.976 126.267 158.042 130.8L134.842 123C136.442 117.933 138.976 113.333 142.442 109.2C146.042 104.933 150.776 101.533 156.642 99C162.642 96.3333 169.909 95 178.442 95C191.509 95 201.842 98.2667 209.442 104.8C217.042 111.333 220.842 120.8 220.842 133.2V170.2C220.842 174.2 222.709 176.2 226.442 176.2H234.442V197H217.642C212.709 197 208.642 195.8 205.442 193.4C202.242 191 200.642 187.8 200.642 183.8V183.6H196.842C196.309 185.2 195.109 187.333 193.242 190C191.376 192.533 188.442 194.8 184.442 196.8C180.442 198.8 174.976 199.8 168.042 199.8ZM172.442 179.4C179.509 179.4 185.242 177.467 189.642 173.6C194.176 169.6 196.442 164.333 196.442 157.8V155.8H171.042C166.376 155.8 162.709 156.8 160.042 158.8C157.376 160.8 156.042 163.6 156.042 167.2C156.042 170.8 157.442 173.733 160.242 176C163.042 178.267 167.109 179.4 172.442 179.4ZM252.867 237V97.8H277.667V109.8H281.267C283.534 105.933 287.067 102.533 291.867 99.6C296.667 96.5333 303.534 95 312.467 95C320.467 95 327.867 97 334.667 101C341.467 104.867 346.934 110.6 351.067 118.2C355.201 125.8 357.267 135 357.267 145.8V149C357.267 159.8 355.201 169 351.067 176.6C346.934 184.2 341.467 190 334.667 194C327.867 197.867 320.467 199.8 312.467 199.8C306.467 199.8 301.401 199.067 297.267 197.6C293.267 196.267 290.001 194.533 287.467 192.4C285.067 190.133 283.134 187.867 281.667 185.6H278.067V237H252.867ZM304.867 177.8C312.734 177.8 319.201 175.333 324.267 170.4C329.467 165.333 332.067 158 332.067 148.4V146.4C332.067 136.8 329.467 129.533 324.267 124.6C319.067 119.533 312.601 117 304.867 117C297.134 117 290.667 119.533 285.467 124.6C280.267 129.533 277.667 136.8 277.667 146.4V148.4C277.667 158 280.267 165.333 285.467 170.4C290.667 175.333 297.134 177.8 304.867 177.8ZM380.406 237V97.8H405.206V109.8H408.806C411.073 105.933 414.606 102.533 419.406 99.6C424.206 96.5333 431.073 95 440.006 95C448.006 95 455.406 97 462.206 101C469.006 104.867 474.473 110.6 478.606 118.2C482.74 125.8 484.806 135 484.806 145.8V149C484.806 159.8 482.74 169 478.606 176.6C474.473 184.2 469.006 190 462.206 194C455.406 197.867 448.006 199.8 440.006 199.8C434.006 199.8 428.94 199.067 424.806 197.6C420.806 196.267 417.54 194.533 415.006 192.4C412.606 190.133 410.673 187.867 409.206 185.6H405.606V237H380.406ZM432.406 177.8C440.273 177.8 446.74 175.333 451.806 170.4C457.006 165.333 459.606 158 459.606 148.4V146.4C459.606 136.8 457.006 129.533 451.806 124.6C446.606 119.533 440.14 117 432.406 117C424.673 117 418.206 119.533 413.006 124.6C407.806 129.533 405.206 136.8 405.206 146.4V148.4C405.206 158 407.806 165.333 413.006 170.4C418.206 175.333 424.673 177.8 432.406 177.8ZM518.745 237V215H572.745C576.479 215 578.345 213 578.345 209V184H574.745C573.679 186.267 572.012 188.533 569.745 190.8C567.479 193.067 564.412 194.933 560.545 196.4C556.679 197.867 551.745 198.6 545.745 198.6C538.012 198.6 531.212 196.867 525.345 193.4C519.612 189.8 515.145 184.867 511.945 178.6C508.745 172.333 507.145 165.133 507.145 157V97.8H532.345V155C532.345 162.467 534.145 168.067 537.745 171.8C541.479 175.533 546.745 177.4 553.545 177.4C561.279 177.4 567.279 174.867 571.545 169.8C575.812 164.6 577.945 157.4 577.945 148.2V97.8H603.145V214.6C603.145 221.4 601.145 226.8 597.145 230.8C593.145 234.933 587.812 237 581.145 237H518.745ZM669.188 197C662.654 197 657.321 195 653.188 191C649.188 186.867 647.188 181.4 647.188 174.6V118.6H622.388V97.8H647.188V67H672.388V97.8H699.588V118.6H672.388V170.2C672.388 174.2 674.254 176.2 677.988 176.2H697.188V197H669.188ZM764.083 199.8C754.216 199.8 745.483 197.733 737.883 193.6C730.416 189.333 724.549 183.4 720.283 175.8C716.149 168.067 714.083 159 714.083 148.6V146.2C714.083 135.8 716.149 126.8 720.283 119.2C724.416 111.467 730.216 105.533 737.683 101.4C745.149 97.1333 753.816 95 763.683 95C773.416 95 781.883 97.2 789.083 101.6C796.283 105.867 801.883 111.867 805.883 119.6C809.883 127.2 811.883 136.067 811.883 146.2V154.8H739.683C739.949 161.6 742.483 167.133 747.283 171.4C752.083 175.667 757.949 177.8 764.883 177.8C771.949 177.8 777.149 176.267 780.483 173.2C783.816 170.133 786.349 166.733 788.083 163L808.683 173.8C806.816 177.267 804.083 181.067 800.483 185.2C797.016 189.2 792.349 192.667 786.483 195.6C780.616 198.4 773.149 199.8 764.083 199.8ZM739.883 136H786.283C785.749 130.267 783.416 125.667 779.283 122.2C775.283 118.733 770.016 117 763.483 117C756.683 117 751.283 118.733 747.283 122.2C743.283 125.667 740.816 130.267 739.883 136ZM865.113 199.8C858.046 199.8 851.713 198.6 846.113 196.2C840.513 193.667 836.046 190.067 832.713 185.4C829.513 180.6 827.913 174.8 827.913 168C827.913 161.2 829.513 155.533 832.713 151C836.046 146.333 840.579 142.867 846.313 140.6C852.179 138.2 858.846 137 866.313 137H893.513V131.4C893.513 126.733 892.046 122.933 889.113 120C886.179 116.933 881.513 115.4 875.113 115.4C868.846 115.4 864.179 116.867 861.113 119.8C858.046 122.6 856.046 126.267 855.113 130.8L831.913 123C833.513 117.933 836.046 113.333 839.513 109.2C843.113 104.933 847.846 101.533 853.713 99C859.713 96.3333 866.979 95 875.513 95C888.579 95 898.913 98.2667 906.513 104.8C914.113 111.333 917.913 120.8 917.913 133.2V170.2C917.913 174.2 919.779 176.2 923.513 176.2H931.513V197H914.713C909.779 197 905.713 195.8 902.513 193.4C899.313 191 897.713 187.8 897.713 183.8V183.6H893.913C893.379 185.2 892.179 187.333 890.313 190C888.446 192.533 885.513 194.8 881.513 196.8C877.513 198.8 872.046 199.8 865.113 199.8ZM869.513 179.4C876.579 179.4 882.313 177.467 886.713 173.6C891.246 169.6 893.513 164.333 893.513 157.8V155.8H868.113C863.446 155.8 859.779 156.8 857.113 158.8C854.446 160.8 853.113 163.6 853.113 167.2C853.113 170.8 854.513 173.733 857.313 176C860.113 178.267 864.179 179.4 869.513 179.4ZM949.938 197V97.8H974.738V108.6H978.338C980.071 105.267 982.938 102.4 986.938 100C990.938 97.4667 996.204 96.2 1002.74 96.2C1009.8 96.2 1015.47 97.6 1019.74 100.4C1024 103.067 1027.27 106.6 1029.54 111H1033.14C1035.4 106.733 1038.6 103.2 1042.74 100.4C1046.87 97.6 1052.74 96.2 1060.34 96.2C1066.47 96.2 1072 97.5333 1076.94 100.2C1082 102.733 1086 106.667 1088.94 112C1092 117.2 1093.54 123.8 1093.54 131.8V197H1068.34V133.6C1068.34 128.133 1066.94 124.067 1064.14 121.4C1061.34 118.6 1057.4 117.2 1052.34 117.2C1046.6 117.2 1042.14 119.067 1038.94 122.8C1035.87 126.4 1034.34 131.6 1034.34 138.4V197H1009.14V133.6C1009.14 128.133 1007.74 124.067 1004.94 121.4C1002.14 118.6 998.204 117.2 993.138 117.2C987.404 117.2 982.938 119.067 979.738 122.8C976.671 126.4 975.138 131.6 975.138 138.4V197H949.938Z"
                            fill="#5336FF"
                          />
                        </svg>
                      </a>
                      <div className="-mr-2 flex items-center md:hidden">
                        <Popover.Button className="inline-flex items-center justify-center rounded-md bg-white p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500">
                          <span className="sr-only">Open main menu</span>
                          <MenuAlt1Icon
                            className="h-6 w-6"
                            aria-hidden="true"
                          />
                        </Popover.Button>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center md:ml-10  md:space-x-8 md:pr-4 desktop-menu">
                    <Popover className="relative">
                      {({ open }) => (
                        <>
                          <Popover.Button
                            className={classNames(
                              open ? "text-gray-900" : "text-gray-500",
                              "group inline-flex items-center rounded-md bg-white text-base font-medium hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                            )}
                          >
                            <span>Solutions</span>
                            <ChevronDownIcon
                              className={classNames(
                                open ? "text-gray-600" : "text-gray-400",
                                "ml-2 h-5 w-5 group-hover:text-gray-500"
                              )}
                              aria-hidden="true"
                            />
                          </Popover.Button>

                          <Transition
                            as={Fragment}
                            enter="transition ease-out duration-200"
                            enterFrom="opacity-0 translate-y-1"
                            enterTo="opacity-100 translate-y-0"
                            leave="transition ease-in duration-150"
                            leaveFrom="opacity-100 translate-y-0"
                            leaveTo="opacity-0 translate-y-1"
                          >
                            <Popover.Panel className="absolute left-1/2 z-10 mt-3 w-screen max-w-md -translate-x-1/2 transform px-2 sm:px-0">
                              <div className="overflow-hidden rounded-lg shadow-lg ring-1 ring-black ring-opacity-5">
                                <div className="relative grid gap-6 bg-white px-5 py-6 sm:gap-8 sm:p-8">
                                  {solutions.map((item) => (
                                    <div
                                      key={item.name}
                                      className="-m-3 flex items-start rounded-lg p-3 transition duration-150 ease-in-out hover:bg-gray-50"
                                    >
                                      <item.icon
                                        className="h-6 w-6 flex-shrink-0 text-indigo-600"
                                        aria-hidden="true"
                                      />
                                      <div className="ml-4">
                                        <p className="text-base font-medium text-gray-900">
                                          {item.name}
                                        </p>
                                        <p className="mt-1 text-sm text-gray-500">
                                          {item.description}
                                        </p>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                                <div className="space-y-6 bg-gray-50 px-5 py-5 sm:flex sm:space-y-0 sm:space-x-10 sm:px-8">
                                  {callsToAction.map((item) => (
                                    <div key={item.name} className="flow-root">
                                      <a
                                        href={item.href}
                                        className="-m-3 flex items-center rounded-md p-3 text-base font-medium text-gray-900 transition duration-150 ease-in-out hover:bg-gray-100"
                                      >
                                        <item.icon
                                          className="h-6 w-6 flex-shrink-0 text-gray-400"
                                          aria-hidden="true"
                                        />
                                        <span className="ml-3">
                                          {item.name}
                                        </span>
                                      </a>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </Popover.Panel>
                          </Transition>
                        </>
                      )}
                    </Popover>

                    <a
                      href="#pricing"
                      className="font-medium text-gray-500 hover:text-gray-900"
                    >
                      Pricing
                    </a>

                    {user?.companyId && (
                      <Link href={`/team/${user?.companyId}`}>
                        <a
                          href="#"
                          className="font-medium text-indigo-600 hover:text-indigo-500"
                        >
                          Dashboard
                        </a>
                      </Link>
                    )}

                    {Object.keys(user).length > 0 && !user?.companyId && (
                      <Link href="/onboarding">
                        <a
                          href="#"
                          className="font-medium text-indigo-600 hover:text-indigo-500"
                        >
                          Setup account
                        </a>
                      </Link>
                    )}

                    {Object.keys(user).length === 0 && (
                      <Link href="/auth">
                        <a
                          href="#"
                          className="font-medium text-indigo-600 hover:text-indigo-500"
                        >
                          Login or Signup
                        </a>
                      </Link>
                    )}
                  </div>
                </nav>
              </div>

              <Transition
                as={Fragment}
                enter="duration-150 ease-out"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="duration-100 ease-in"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Popover.Panel
                  focus
                  className="absolute inset-x-0 top-0 z-10 origin-top-right transform p-2 transition md:hidden"
                >
                  <div className="overflow-hidden rounded-lg bg-white shadow-md ring-1 ring-black ring-opacity-5">
                    <div className="flex items-center justify-between px-5 pt-4">
                      <div>
                        <img
                          className="h-8 w-auto"
                          src="https://tailwindui.com/img/logos/mark.svg?color=indigo&shade=600"
                          alt=""
                        />
                      </div>
                      <div className="-mr-2">
                        <Popover.Button className="inline-flex items-center justify-center rounded-md bg-white p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500">
                          <span className="sr-only">Close main menu</span>
                          <XIcon className="h-6 w-6" aria-hidden="true" />
                        </Popover.Button>
                      </div>
                    </div>
                    <div className="space-y-1 px-2 pt-2 pb-3 md:hidden">
                      <a
                        href="#features"
                        className="block rounded-md px-3 py-2 text-base font-medium text-gray-700 hover:bg-gray-50 hover:text-gray-900"
                      >
                        Solutions
                      </a>
                      <a
                        href="#pricing"
                        className="block rounded-md px-3 py-2 text-base font-medium text-gray-700 hover:bg-gray-50 hover:text-gray-900"
                      >
                        Pricing
                      </a>
                    </div>

                    <div className="p-4 bg-gray-100 text-center">
                      {user?.companyId ? (
                        <Link href={`/team/${user?.companyId}`}>
                          <a className="font-medium text-indigo-600 hover:text-indigo-500">
                            Go to Dashboard
                          </a>
                        </Link>
                      ) : (
                        <Link href={`/auth?email=${email}`}>
                          <a className="font-medium text-indigo-600 hover:text-indigo-500">
                            Login / Signup
                          </a>
                        </Link>
                      )}
                    </div>
                  </div>
                </Popover.Panel>
              </Transition>
            </Popover>

            <main className="mx-auto mt-10 max-w-7xl px-4 sm:mt-12 sm:px-6 md:mt-16 lg:mt-20 lg:px-8 xl:mt-28">
              <div className="sm:text-center lg:text-left">
                <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl md:text-6xl">
                  <span className=" xl:inline">Amplify the voice</span>{" "}
                  <span className=" text-indigo-600 xl:inline">
                    of your organization
                  </span>
                </h1>
                <p className="mt-3 text-base text-gray-500 sm:mx-auto sm:mt-5 sm:max-w-xl sm:text-lg md:mt-5 md:text-xl lg:mx-0">
                  We empower leaders and their teams by providing an effective
                  way to share ideas, collect feedback, and take action on what
                  matters most.
                </p>
                <div className="mt-5 sm:mt-8 sm:flex sm:justify-center lg:justify-start max-w-lg cta-buttons">
                  <input
                    className="flex-1 py-3 w-full mr-2 border rounded px-3 focus:outline-none focus:ring focus:ring-indigo-100 placeholder:text-slate-400"
                    placeholder="Email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  <div className="rounded-md shadow sm:space-y-2 get-started-button">
                    <Link href={`/auth?email=${email}`}>
                      <a className="flex w-full items-center justify-center rounded-md border border-transparent bg-indigo-600 px-8 py-3 text-base font-medium text-white hover:bg-indigo-700 md:py-4 md:px-10 md:text-lg">
                        Get started
                      </a>
                    </Link>
                  </div>
                </div>
                <div className="mt-3 text-sm text-gray-400 ">
                  Includes 7-day free trial. No credit card required.
                </div>
              </div>
            </main>
          </div>
        </div>
        <div className="lg:absolute lg:inset-y-0 lg:right-0 lg:w-1/2 flex items-center justify-center">
          <img
            className="h-56 w-full object-cover sm:h-72 md:h-96 lg:h-full lg:w-full"
            src="https://images.unsplash.com/photo-1664575599736-c5197c684128?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1470&q=80"
            alt=""
          />
        </div>
      </div>

      {/* TESTIMONIAL */}
      <section className="overflow-hidden bg-white py-12 md:py-20 lg:py-24">
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <svg
            className="absolute top-full right-full translate-x-1/3 -translate-y-1/4 transform lg:translate-x-1/2 xl:-translate-y-1/2"
            width={404}
            height={404}
            fill="none"
            viewBox="0 0 404 404"
            role="img"
            aria-labelledby="svg-workcation"
          >
            <title id="svg-workcation">Workcation</title>
            <defs>
              <pattern
                id="ad119f34-7694-4c31-947f-5c9d249b21f3"
                x={0}
                y={0}
                width={20}
                height={20}
                patternUnits="userSpaceOnUse"
              >
                <rect
                  x={0}
                  y={0}
                  width={4}
                  height={4}
                  className="text-gray-200"
                  fill="currentColor"
                />
              </pattern>
            </defs>
            <rect
              width={404}
              height={404}
              fill="url(#ad119f34-7694-4c31-947f-5c9d249b21f3)"
            />
          </svg>

          <div className="relative">
            <img
              className="mx-auto h-12"
              src="https://captivation.agency/wp-content/uploads/2020/09/Captivation-Logo.svg"
              alt="Captivation"
            />
            <blockquote className="mt-6">
              <div className="mx-auto max-w-3xl text-center text-2xl font-medium leading-9 text-gray-900">
                <p>
                  &ldquo;Positive employee morale is crucial to our success as
                  an organization. HappyTeam enables us to create moments in
                  time each week for us to ensure we are staying aware and
                  proactive in that effort.&rdquo;
                </p>
              </div>
              <footer className="mt-8">
                <div className="md:flex md:items-center md:justify-center">
                  <div className="mt-3 text-center md:mt-0 md:ml-4 md:flex md:items-center">
                    <div className="text-base font-medium text-gray-900">
                      Jim Martin
                    </div>

                    <svg
                      className="mx-1 hidden h-5 w-5 text-indigo-600 md:block"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M11 0h3L9 20H6l5-20z" />
                    </svg>

                    <div className="text-base font-medium text-gray-500">
                      CEO,{" "}
                      <a
                        target="blank"
                        className="text-indigo-600"
                        href="https://captivation.agency/"
                      >
                        Captivation Agency
                      </a>
                    </div>
                  </div>
                </div>
              </footer>
            </blockquote>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <div id="features" className="overflow-hidden bg-gray-50 py-16 lg:py-24">
        <div className="relative mx-auto max-w-xl px-4 sm:px-6 lg:max-w-7xl lg:px-8">
          <svg
            className="absolute left-full hidden -translate-x-1/2 -translate-y-1/4 transform lg:block"
            width={404}
            height={784}
            fill="none"
            viewBox="0 0 404 784"
            aria-hidden="true"
          >
            <defs>
              <pattern
                id="b1e6e422-73f8-40a6-b5d9-c8586e37e0e7"
                x={0}
                y={0}
                width={20}
                height={20}
                patternUnits="userSpaceOnUse"
              >
                <rect
                  x={0}
                  y={0}
                  width={4}
                  height={4}
                  className="text-gray-200"
                  fill="currentColor"
                />
              </pattern>
            </defs>
            <rect
              width={404}
              height={784}
              fill="url(#b1e6e422-73f8-40a6-b5d9-c8586e37e0e7)"
            />
          </svg>

          <div className="relative">
            <h2 className="text-center text-3xl font-bold leading-8 tracking-tight text-gray-900 sm:text-4xl">
              Save weeks unpacking insight from your pulse surveys
            </h2>
            <p className="mx-auto mt-4 max-w-3xl text-center text-xl text-gray-500">
              Designed for people-ops teams and managers, HappyTeam leverages ai
              and machine learning to help companies collect deep insight from
              employees, easily find meaning, and take action instantly.
            </p>
          </div>

          <div className="relative mt-12 lg:mt-24 lg:grid lg:grid-cols-2 lg:items-center lg:gap-8">
            <div className="relative">
              <h3 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">
                Tools to paint the full picture and <br /> save you a{" "}
                <span className="italic">heck</span> of a lot of time.
              </h3>
              <p className="mt-3 text-gray-500">
                Insight discovery takes a lot of time. You spends weeks putting
                your engagement surveys together, and often just as long
                categorizing and tagging responses to derive meaning. With
                HappyTeam, you&apos;ll you certainly save time, but most
                importantly, be able to focus your efforts on what matters most
                – <b>taking care of your people. </b>
              </p>

              <dl className="mt-10 space-y-10">
                {transferFeatures.map((item) => (
                  <div key={item.id} className="relative">
                    <dt>
                      <div className="absolute flex h-12 w-12 items-center justify-center rounded-md bg-indigo-500 text-white">
                        <item.icon className="h-6 w-6" aria-hidden="true" />
                      </div>
                      <p className="ml-16 text-lg font-medium leading-6 text-gray-900">
                        {item.name}
                      </p>
                    </dt>
                    <dd className="mt-2 ml-16 text-base text-gray-500">
                      {item.description}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>

            <div className="relative -mx-4 mt-10 lg:mt-0" aria-hidden="true">
              <svg
                className="absolute left-1/2 -translate-x-1/2 translate-y-16 transform lg:hidden"
                width={784}
                height={404}
                fill="none"
                viewBox="0 0 784 404"
              >
                <defs>
                  <pattern
                    id="ca9667ae-9f92-4be7-abcb-9e3d727f2941"
                    x={0}
                    y={0}
                    width={20}
                    height={20}
                    patternUnits="userSpaceOnUse"
                  >
                    <rect
                      x={0}
                      y={0}
                      width={4}
                      height={4}
                      className="text-gray-200"
                      fill="currentColor"
                    />
                  </pattern>
                </defs>
                <rect
                  width={784}
                  height={404}
                  fill="url(#ca9667ae-9f92-4be7-abcb-9e3d727f2941)"
                />
              </svg>
              <img
                className="relative mx-auto"
                width={490}
                src="/Feature_Flow.png"
                alt=""
              />
            </div>
          </div>

          <svg
            className="absolute right-full hidden translate-x-1/2 translate-y-12 transform lg:block"
            width={404}
            height={784}
            fill="none"
            viewBox="0 0 404 784"
            aria-hidden="true"
          >
            <defs>
              <pattern
                id="64e643ad-2176-4f86-b3d7-f2c5da3b6a6d"
                x={0}
                y={0}
                width={20}
                height={20}
                patternUnits="userSpaceOnUse"
              >
                <rect
                  x={0}
                  y={0}
                  width={4}
                  height={4}
                  className="text-gray-200"
                  fill="currentColor"
                />
              </pattern>
            </defs>
            <rect
              width={404}
              height={784}
              fill="url(#64e643ad-2176-4f86-b3d7-f2c5da3b6a6d)"
            />
          </svg>

          <div className="relative mt-12 sm:mt-16 lg:mt-24">
            <div className="lg:grid lg:grid-flow-row-dense lg:grid-cols-2 lg:items-center lg:gap-8">
              <div className="lg:col-start-2">
                <h3 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">
                  Incentivize your team
                </h3>
                <p className="mt-3 text-lg text-gray-500">
                  Unpacking actionable insights is hard.. but getting teams to
                  actively participate (and want to) can be even harder.
                </p>

                <dl className="mt-10 space-y-10">
                  {communicationFeatures.map((item) => (
                    <div key={item.id} className="relative">
                      <dt>
                        <div className="absolute flex h-12 w-12 items-center justify-center rounded-md bg-indigo-500 text-white">
                          <item.icon className="h-6 w-6" aria-hidden="true" />
                        </div>
                        <p className="ml-16 text-lg font-medium leading-6 text-gray-900">
                          {item.name}
                        </p>
                      </dt>
                      <dd className="mt-2 ml-16 text-base text-gray-500">
                        {item.description}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>

              <div className="relative -mx-4 mt-10 lg:col-start-1 lg:mt-0">
                <svg
                  className="absolute left-1/2 -translate-x-1/2 translate-y-16 transform lg:hidden"
                  width={784}
                  height={404}
                  fill="none"
                  viewBox="0 0 784 404"
                  aria-hidden="true"
                >
                  <defs>
                    <pattern
                      id="e80155a9-dfde-425a-b5ea-1f6fadd20131"
                      x={0}
                      y={0}
                      width={20}
                      height={20}
                      patternUnits="userSpaceOnUse"
                    >
                      <rect
                        x={0}
                        y={0}
                        width={4}
                        height={4}
                        className="text-gray-200"
                        fill="currentColor"
                      />
                    </pattern>
                  </defs>
                  <rect
                    width={784}
                    height={404}
                    fill="url(#e80155a9-dfde-425a-b5ea-1f6fadd20131)"
                  />
                </svg>
                <img
                  className="relative mx-auto"
                  width={490}
                  src="/rewards.png"
                  alt=""
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="bg-indigo-700">
        <div className="mx-auto max-w-2xl py-16 px-4 text-center sm:py-20 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
            <span className="block">
              The best way to keep a pulse on your team&apos;s well-being
            </span>
          </h2>
          <p className="mt-4 text-lg leading-6 text-indigo-200">
            Amplify your team&apos;s voice with HappyTeam.
          </p>

          <Link href={`/auth?email=${email}`}>
            <a className="mt-8 inline-flex w-full items-center justify-center rounded-md border border-transparent bg-white px-5 py-3 text-base font-medium text-indigo-600 hover:bg-indigo-50 sm:w-auto">
              Get started
            </a>
          </Link>
        </div>
      </div>

      {/* PRICING */}
      <div id="pricing" className="bg-white">
        <div className="mx-auto max-w-7xl py-16 px-4 sm:py-24 sm:px-6 lg:px-8">
          <div className="pb-16 xl:flex xl:items-center xl:justify-between">
            <div>
              <h1 className="text-4xl font-bold tracking-tight sm:text-5xl">
                <span className="text-gray-900">Everything you need for </span>
                <span className="text-indigo-600">$9.99 a month</span>
              </h1>
              <p className="mt-5 text-xl text-gray-500">
                Includes every feature we offer plus unlimited surveys, segments
                and templates.
              </p>
              <p className="mt-2 text-sm text-gray-500">
                Need more than 249 recipient seats?{" "}
                <a
                  href="mailto:christian@happyteam.ai"
                  className="text-indigo-600"
                >
                  Contact sales
                </a>
              </p>
            </div>

            <Link href={`/auth?email=${email}`}>
              <a className="mt-8 inline-flex w-full items-center justify-center rounded-md border border-transparent bg-indigo-600 px-5 py-3 text-base font-medium text-white hover:bg-indigo-700 sm:mt-10 sm:w-auto xl:mt-0">
                Start your free trial
              </a>
            </Link>
          </div>
          <div className="border-t border-gray-200 pt-16 xl:grid xl:grid-cols-3 xl:gap-x-8">
            <div>
              {/* <h2 className="text-lg font-semibold text-indigo-600">
                Plan details
              </h2> */}
              <p className="mt-2 text-3xl font-bold tracking-tight text-indigo-600">
                Plan details
              </p>
              <p className="mt-4 text-lg text-gray-500">
                Your plan includes new features we develop at no added cost.
                Your pricing plan is locked in under 249 team members.
              </p>
            </div>
            <div className="mt-4 sm:mt-8 md:mt-10 md:grid md:grid-cols-2 md:gap-x-8 xl:col-span-2 xl:mt-0">
              <ul role="list" className="divide-y divide-gray-200">
                {features.slice(0, 5).map((feature, featureIdx) =>
                  featureIdx === 0 ? (
                    <li key={feature} className="flex py-4 md:py-0 md:pb-4">
                      <CheckIcon
                        className="h-6 w-6 flex-shrink-0 text-green-500"
                        aria-hidden="true"
                      />
                      <span className="ml-3 text-base text-gray-500">
                        {feature}
                      </span>
                    </li>
                  ) : (
                    <li key={feature} className="flex py-4">
                      <CheckIcon
                        className="h-6 w-6 flex-shrink-0 text-green-500"
                        aria-hidden="true"
                      />
                      <span className="ml-3 text-base text-gray-500">
                        {feature}
                      </span>
                    </li>
                  )
                )}
              </ul>
              <ul
                role="list"
                className="divide-y divide-gray-200 border-t border-gray-200 md:border-t-0"
              >
                {features.slice(5).map((feature, featureIdx) =>
                  featureIdx === 0 ? (
                    <li
                      key={feature}
                      className="flex py-4 md:border-t-0 md:py-0 md:pb-4"
                    >
                      <CheckIcon
                        className="h-6 w-6 flex-shrink-0 text-green-500"
                        aria-hidden="true"
                      />
                      <span className="ml-3 text-base text-gray-500">
                        {feature}
                      </span>
                    </li>
                  ) : (
                    <li key={feature} className="flex py-4">
                      <CheckIcon
                        className="h-6 w-6 flex-shrink-0 text-green-500"
                        aria-hidden="true"
                      />
                      <span className="ml-3 text-base text-gray-500">
                        {feature}
                      </span>
                    </li>
                  )
                )}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* FAQS */}
      {/* <div className="bg-indigo-700">
        <div className="mx-auto max-w-7xl py-16 px-4 sm:py-24 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold tracking-tight text-white">
            Frequently asked questions
          </h2>
          <div className="mt-6 border-t border-indigo-300 border-opacity-25 pt-10">
            <dl className="space-y-10 md:grid md:grid-cols-2 md:gap-x-8 md:gap-y-12 md:space-y-0">
              {faqs.map((faq) => (
                <div key={faq.id}>
                  <dt className="text-lg font-medium leading-6 text-white">
                    {faq.question}
                  </dt>
                  <dd className="mt-2 text-base text-indigo-200">
                    {faq.answer}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </div> */}

      <footer className="bg-gray-50" aria-labelledby="footer-heading">
        <h2 id="footer-heading" className="sr-only">
          Footer
        </h2>
        <div className="mx-auto max-w-7xl py-12 px-4 sm:px-6 lg:py-16 lg:px-8">
          <div className="xl:grid xl:grid-cols-3 xl:gap-8">
            <div className="space-y-8 xl:col-span-1">
              <a href="#">
                <svg
                  style={{ height: "40px" }}
                  viewBox="0 0 1107 284"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M429 256C395.732 279.491 357.761 277.827 323 256"
                    stroke="#5336FF"
                    strokeWidth="21"
                    strokeLinecap="round"
                  />
                  <path
                    d="M14 197V57H39.2V110H42.8C43.8667 107.867 45.5333 105.733 47.8 103.6C50.0667 101.467 53.0667 99.7333 56.8 98.4C60.6667 96.9333 65.5333 96.2 71.4 96.2C79.1333 96.2 85.8667 98 91.6 101.6C97.4667 105.067 102 109.933 105.2 116.2C108.4 122.333 110 129.533 110 137.8V197H84.8V139.8C84.8 132.333 82.9333 126.733 79.2 123C75.6 119.267 70.4 117.4 63.6 117.4C55.8667 117.4 49.8667 120 45.6 125.2C41.3333 130.267 39.2 137.4 39.2 146.6V197H14ZM168.042 199.8C160.976 199.8 154.642 198.6 149.042 196.2C143.442 193.667 138.976 190.067 135.642 185.4C132.442 180.6 130.842 174.8 130.842 168C130.842 161.2 132.442 155.533 135.642 151C138.976 146.333 143.509 142.867 149.242 140.6C155.109 138.2 161.776 137 169.242 137H196.442V131.4C196.442 126.733 194.976 122.933 192.042 120C189.109 116.933 184.442 115.4 178.042 115.4C171.776 115.4 167.109 116.867 164.042 119.8C160.976 122.6 158.976 126.267 158.042 130.8L134.842 123C136.442 117.933 138.976 113.333 142.442 109.2C146.042 104.933 150.776 101.533 156.642 99C162.642 96.3333 169.909 95 178.442 95C191.509 95 201.842 98.2667 209.442 104.8C217.042 111.333 220.842 120.8 220.842 133.2V170.2C220.842 174.2 222.709 176.2 226.442 176.2H234.442V197H217.642C212.709 197 208.642 195.8 205.442 193.4C202.242 191 200.642 187.8 200.642 183.8V183.6H196.842C196.309 185.2 195.109 187.333 193.242 190C191.376 192.533 188.442 194.8 184.442 196.8C180.442 198.8 174.976 199.8 168.042 199.8ZM172.442 179.4C179.509 179.4 185.242 177.467 189.642 173.6C194.176 169.6 196.442 164.333 196.442 157.8V155.8H171.042C166.376 155.8 162.709 156.8 160.042 158.8C157.376 160.8 156.042 163.6 156.042 167.2C156.042 170.8 157.442 173.733 160.242 176C163.042 178.267 167.109 179.4 172.442 179.4ZM252.867 237V97.8H277.667V109.8H281.267C283.534 105.933 287.067 102.533 291.867 99.6C296.667 96.5333 303.534 95 312.467 95C320.467 95 327.867 97 334.667 101C341.467 104.867 346.934 110.6 351.067 118.2C355.201 125.8 357.267 135 357.267 145.8V149C357.267 159.8 355.201 169 351.067 176.6C346.934 184.2 341.467 190 334.667 194C327.867 197.867 320.467 199.8 312.467 199.8C306.467 199.8 301.401 199.067 297.267 197.6C293.267 196.267 290.001 194.533 287.467 192.4C285.067 190.133 283.134 187.867 281.667 185.6H278.067V237H252.867ZM304.867 177.8C312.734 177.8 319.201 175.333 324.267 170.4C329.467 165.333 332.067 158 332.067 148.4V146.4C332.067 136.8 329.467 129.533 324.267 124.6C319.067 119.533 312.601 117 304.867 117C297.134 117 290.667 119.533 285.467 124.6C280.267 129.533 277.667 136.8 277.667 146.4V148.4C277.667 158 280.267 165.333 285.467 170.4C290.667 175.333 297.134 177.8 304.867 177.8ZM380.406 237V97.8H405.206V109.8H408.806C411.073 105.933 414.606 102.533 419.406 99.6C424.206 96.5333 431.073 95 440.006 95C448.006 95 455.406 97 462.206 101C469.006 104.867 474.473 110.6 478.606 118.2C482.74 125.8 484.806 135 484.806 145.8V149C484.806 159.8 482.74 169 478.606 176.6C474.473 184.2 469.006 190 462.206 194C455.406 197.867 448.006 199.8 440.006 199.8C434.006 199.8 428.94 199.067 424.806 197.6C420.806 196.267 417.54 194.533 415.006 192.4C412.606 190.133 410.673 187.867 409.206 185.6H405.606V237H380.406ZM432.406 177.8C440.273 177.8 446.74 175.333 451.806 170.4C457.006 165.333 459.606 158 459.606 148.4V146.4C459.606 136.8 457.006 129.533 451.806 124.6C446.606 119.533 440.14 117 432.406 117C424.673 117 418.206 119.533 413.006 124.6C407.806 129.533 405.206 136.8 405.206 146.4V148.4C405.206 158 407.806 165.333 413.006 170.4C418.206 175.333 424.673 177.8 432.406 177.8ZM518.745 237V215H572.745C576.479 215 578.345 213 578.345 209V184H574.745C573.679 186.267 572.012 188.533 569.745 190.8C567.479 193.067 564.412 194.933 560.545 196.4C556.679 197.867 551.745 198.6 545.745 198.6C538.012 198.6 531.212 196.867 525.345 193.4C519.612 189.8 515.145 184.867 511.945 178.6C508.745 172.333 507.145 165.133 507.145 157V97.8H532.345V155C532.345 162.467 534.145 168.067 537.745 171.8C541.479 175.533 546.745 177.4 553.545 177.4C561.279 177.4 567.279 174.867 571.545 169.8C575.812 164.6 577.945 157.4 577.945 148.2V97.8H603.145V214.6C603.145 221.4 601.145 226.8 597.145 230.8C593.145 234.933 587.812 237 581.145 237H518.745ZM669.188 197C662.654 197 657.321 195 653.188 191C649.188 186.867 647.188 181.4 647.188 174.6V118.6H622.388V97.8H647.188V67H672.388V97.8H699.588V118.6H672.388V170.2C672.388 174.2 674.254 176.2 677.988 176.2H697.188V197H669.188ZM764.083 199.8C754.216 199.8 745.483 197.733 737.883 193.6C730.416 189.333 724.549 183.4 720.283 175.8C716.149 168.067 714.083 159 714.083 148.6V146.2C714.083 135.8 716.149 126.8 720.283 119.2C724.416 111.467 730.216 105.533 737.683 101.4C745.149 97.1333 753.816 95 763.683 95C773.416 95 781.883 97.2 789.083 101.6C796.283 105.867 801.883 111.867 805.883 119.6C809.883 127.2 811.883 136.067 811.883 146.2V154.8H739.683C739.949 161.6 742.483 167.133 747.283 171.4C752.083 175.667 757.949 177.8 764.883 177.8C771.949 177.8 777.149 176.267 780.483 173.2C783.816 170.133 786.349 166.733 788.083 163L808.683 173.8C806.816 177.267 804.083 181.067 800.483 185.2C797.016 189.2 792.349 192.667 786.483 195.6C780.616 198.4 773.149 199.8 764.083 199.8ZM739.883 136H786.283C785.749 130.267 783.416 125.667 779.283 122.2C775.283 118.733 770.016 117 763.483 117C756.683 117 751.283 118.733 747.283 122.2C743.283 125.667 740.816 130.267 739.883 136ZM865.113 199.8C858.046 199.8 851.713 198.6 846.113 196.2C840.513 193.667 836.046 190.067 832.713 185.4C829.513 180.6 827.913 174.8 827.913 168C827.913 161.2 829.513 155.533 832.713 151C836.046 146.333 840.579 142.867 846.313 140.6C852.179 138.2 858.846 137 866.313 137H893.513V131.4C893.513 126.733 892.046 122.933 889.113 120C886.179 116.933 881.513 115.4 875.113 115.4C868.846 115.4 864.179 116.867 861.113 119.8C858.046 122.6 856.046 126.267 855.113 130.8L831.913 123C833.513 117.933 836.046 113.333 839.513 109.2C843.113 104.933 847.846 101.533 853.713 99C859.713 96.3333 866.979 95 875.513 95C888.579 95 898.913 98.2667 906.513 104.8C914.113 111.333 917.913 120.8 917.913 133.2V170.2C917.913 174.2 919.779 176.2 923.513 176.2H931.513V197H914.713C909.779 197 905.713 195.8 902.513 193.4C899.313 191 897.713 187.8 897.713 183.8V183.6H893.913C893.379 185.2 892.179 187.333 890.313 190C888.446 192.533 885.513 194.8 881.513 196.8C877.513 198.8 872.046 199.8 865.113 199.8ZM869.513 179.4C876.579 179.4 882.313 177.467 886.713 173.6C891.246 169.6 893.513 164.333 893.513 157.8V155.8H868.113C863.446 155.8 859.779 156.8 857.113 158.8C854.446 160.8 853.113 163.6 853.113 167.2C853.113 170.8 854.513 173.733 857.313 176C860.113 178.267 864.179 179.4 869.513 179.4ZM949.938 197V97.8H974.738V108.6H978.338C980.071 105.267 982.938 102.4 986.938 100C990.938 97.4667 996.204 96.2 1002.74 96.2C1009.8 96.2 1015.47 97.6 1019.74 100.4C1024 103.067 1027.27 106.6 1029.54 111H1033.14C1035.4 106.733 1038.6 103.2 1042.74 100.4C1046.87 97.6 1052.74 96.2 1060.34 96.2C1066.47 96.2 1072 97.5333 1076.94 100.2C1082 102.733 1086 106.667 1088.94 112C1092 117.2 1093.54 123.8 1093.54 131.8V197H1068.34V133.6C1068.34 128.133 1066.94 124.067 1064.14 121.4C1061.34 118.6 1057.4 117.2 1052.34 117.2C1046.6 117.2 1042.14 119.067 1038.94 122.8C1035.87 126.4 1034.34 131.6 1034.34 138.4V197H1009.14V133.6C1009.14 128.133 1007.74 124.067 1004.94 121.4C1002.14 118.6 998.204 117.2 993.138 117.2C987.404 117.2 982.938 119.067 979.738 122.8C976.671 126.4 975.138 131.6 975.138 138.4V197H949.938Z"
                    fill="#5336FF"
                  />
                </svg>
              </a>
              <p className="text-base text-gray-500">
                Amplify the voice of your organization.
              </p>
              <div className="flex space-x-6">
                {footerNav.social.map((item) => (
                  <a
                    key={item.name}
                    href={item.href}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <span className="sr-only">{item.name}</span>
                    <item.icon className="h-6 w-6" aria-hidden="true" />
                  </a>
                ))}
              </div>
            </div>
            <div className="mt-12 grid grid-cols-2 gap-8 xl:col-span-2 xl:mt-0">
              <div className="md:grid md:grid-cols-2 md:gap-8">
                {/* <div>
                  <h3 className="text-base font-medium text-gray-900">
                    Solutions
                  </h3>
                  <ul role="list" className="mt-4 space-y-4">
                    {footerNav.solutions.map((item) => (
                      <li key={item.name}>
                        <a
                          href={item.href}
                          className="text-base text-gray-500 hover:text-gray-900"
                        >
                          {item.name}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div> */}
                {/* <div className="mt-12 md:mt-0">
                  <h3 className="text-base font-medium text-gray-900">
                    Support
                  </h3>
                  <ul role="list" className="mt-4 space-y-4">
                    {footerNav.support.map((item) => (
                      <li key={item.name}>
                        <a
                          href={item.href}
                          className="text-base text-gray-500 hover:text-gray-900"
                        >
                          {item.name}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div> */}
              </div>
              <div className="md:grid md:grid-cols-2 md:gap-8">
                <div className="mt-12 md:mt-0">
                  <h3 className="text-base font-medium text-gray-900">Legal</h3>
                  <ul role="list" className="mt-4 space-y-4">
                    <li>
                      <Link
                        href="/privacy"
                        className="text-base text-gray-500 hover:text-gray-900"
                      >
                        Privacy Policy
                      </Link>
                    </li>

                    <li>
                      <Link
                        href="/terms"
                        className="text-base text-gray-500 hover:text-gray-900"
                      >
                        Terms of Service
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-12 border-t border-gray-200 pt-8">
            <p className="text-base text-gray-400 xl:text-center">
              &copy; {new Date().getFullYear()} HappyTeam, Inc. All rights
              reserved.
            </p>
          </div>
        </div>
      </footer>

      <div
        className="fixed inset-x-0 bottom-0 pb-2 sm:pb-5"
        style={{ zIndex: "100", display: "none" }}
      >
        <div className="mx-auto max-w-7xl px-2 sm:px-6 lg:px-8">
          <div className="rounded-lg bg-indigo-600 p-2 shadow-lg sm:p-3">
            <div className="flex flex-wrap items-center justify-between">
              <div className="flex w-0 flex-1 items-center">
                <span className="flex rounded-lg bg-indigo-800 p-2">
                  <SpeakerphoneIcon
                    className="h-6 w-6 text-white"
                    aria-hidden="true"
                  />
                </span>
                <p className="ml-3 truncate font-medium text-white">
                  <span className="md:hidden">We announced a new product!</span>
                  <span className="hidden md:inline">
                    Big news! We are live – signup today for 15% off
                  </span>
                </p>
              </div>
              <div className="order-3 mt-2 w-full flex-shrink-0 sm:order-2 sm:mt-0 sm:w-auto">
                <a
                  href="#"
                  className="flex items-center justify-center rounded-md border border-transparent bg-white px-4 py-2 text-sm font-medium text-indigo-600 shadow-sm hover:bg-indigo-50"
                >
                  Start free trial
                </a>
              </div>
              <div className="order-2 flex-shrink-0 sm:order-3 sm:ml-2">
                <button
                  type="button"
                  className="-mr-1 flex rounded-md p-2 hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-white"
                >
                  <span className="sr-only">Dismiss</span>
                  <XIcon className="h-6 w-6 text-white" aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
